import {
  Activity,
  Archive,
  BrainCircuit,
  Briefcase,
  CalendarDays,
  CheckCircle2,
  Clock3,
  Code2,
  ExternalLink,
  FolderGit2,
  GitBranch,
  GitFork,
  Globe2,
  Layers3,
  Lightbulb,
  ListChecks,
  LoaderCircle,
  PieChart as PieChartIcon,
  RefreshCw,
  Rocket,
  Sparkles,
  Star,
  Target,
  TrendingUp,
  UserRound,
  Users,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useOutletContext } from 'react-router-dom'
import {
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from 'recharts'
import {
  getGithubAnalysis,
  refreshGithubAnalysis,
} from '../api.js'
import { userGithubRepositoryAssistantContext } from '../assistant-context.js'
import { useAuth } from '../auth-context.js'
import EmptyState from '../components/EmptyState.jsx'
import ErrorState from '../components/ErrorState.jsx'
import LoadingState from '../components/LoadingState.jsx'
import {
  Badge,
  Button,
  StatsCard,
} from '../components/ui/index.js'
import { NOINDEX_ROBOTS, setPageMetadata } from '../page-metadata.js'
import { markdownToSafeHtml } from '../security/sanitize-html.js'

const UPDATE_NOTICE = 'GitHub 分析结果不会自动更新。如果你的仓库、Star、语言占比或项目内容发生变化，请点击刷新分析重新生成。'
const RECENT_ACTIVE_DAYS = 180
const CHART_COLORS = [
  '#2563eb',
  '#16a34a',
  '#f59e0b',
  '#dc2626',
  '#7c3aed',
  '#0891b2',
  '#ea580c',
  '#475569',
]

function cleanText(value) {
  return typeof value === 'string' && value.trim() ? value.trim() : ''
}

function normalizeDateValue(value) {
  if (!value) return ''
  return typeof value === 'string' && !value.includes('T')
    ? value.replace(' ', 'T')
    : value
}

function getTime(value) {
  const date = new Date(normalizeDateValue(value))
  return Number.isNaN(date.getTime()) ? 0 : date.getTime()
}

function formatNumber(value) {
  return new Intl.NumberFormat('zh-CN').format(Number(value) || 0)
}

function formatDate(value) {
  if (!value) return ''
  const date = new Date(normalizeDateValue(value))
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10)
  return new Intl.DateTimeFormat('zh-CN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date)
}

function formatDateTime(value) {
  if (!value) return ''
  const date = new Date(normalizeDateValue(value))
  if (Number.isNaN(date.getTime())) return String(value)
  return new Intl.DateTimeFormat('zh-CN', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date)
}

function externalUrl(value) {
  const url = cleanText(value)
  if (!url) return ''
  return /^https?:\/\//i.test(url) ? url : `https://${url}`
}

function repoLabel(repo) {
  return repo?.fullName || repo?.name || '未命名仓库'
}

function normalizeStringList(value) {
  return Array.isArray(value)
    ? value
      .map((item) => {
        if (typeof item === 'string') return cleanText(item)
        if (!item || typeof item !== 'object') return ''
        return cleanText(item.text || item.title || item.content || item.reason)
      })
      .filter(Boolean)
    : []
}

function MarkdownBlock({ className = '', content, fallback }) {
  const text = cleanText(content) || cleanText(fallback)
  const html = useMemo(() => {
    if (!text) return ''
    return markdownToSafeHtml(text)
  }, [text])

  if (!text) return null

  return (
    <div
      className={`github-markdown ${className}`}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  )
}

function ReportSection({
  children,
  className = '',
  description,
  icon,
  title,
}) {
  return (
    <section className={`github-report-card ${className}`}>
      <header className="github-report-section-header">
        <span>{icon}</span>
        <div>
          <h2>{title}</h2>
          {description && <p>{description}</p>}
        </div>
      </header>
      {children}
    </section>
  )
}

function SimpleList({ emptyText, items }) {
  const normalizedItems = normalizeStringList(items)
  if (!normalizedItems.length) {
    return <p className="github-report-empty-text">{emptyText}</p>
  }
  return (
    <ul className="github-report-list">
      {normalizedItems.map((item, index) => (
        <li key={`${item}-${index}`}>{item}</li>
      ))}
    </ul>
  )
}

function getLanguageStats(repos) {
  const counts = new Map()
  for (const repo of repos || []) {
    if (!repo.language) continue
    counts.set(repo.language, (counts.get(repo.language) || 0) + 1)
  }
  const total = [...counts.values()].reduce((sum, count) => sum + count, 0)
  return [...counts.entries()]
    .map(([language, count]) => ({
      language,
      count,
      percentage: total ? Math.round((count / total) * 100) : 0,
    }))
    .sort((first, second) => second.count - first.count)
    .slice(0, 8)
}

function getResumeRepositories(analysis, repos) {
  const repoByFullName = new Map(
    repos
      .filter((repo) => repo.fullName)
      .map((repo) => [repo.fullName, repo]),
  )

  return (analysis.standoutRepositories || [])
    .map((item) => {
      const fullName = typeof item === 'string'
        ? cleanText(item)
        : cleanText(item?.fullName || item?.repo)
      if (!fullName) return null
      const repo = repoByFullName.get(fullName) || {
        fullName,
        name: fullName.split('/').pop(),
        htmlUrl: `https://github.com/${fullName}`,
      }
      return {
        ...repo,
        resumeAngle: cleanText(
          item?.resumeAngle
            || item?.resumeValue
            || item?.angle
            || item?.highlight,
        ),
        resumeReason: cleanText(item?.reason),
      }
    })
    .filter(Boolean)
    .slice(0, 6)
}

function getProfileFacts(profile, repos) {
  const blogUrl = externalUrl(profile.blog)
  return [
    profile.publicRepos || repos.length
      ? { label: '公开仓库', value: formatNumber(profile.publicRepos || repos.length) }
      : null,
    profile.followers || profile.followers === 0
      ? { label: 'Followers', value: formatNumber(profile.followers) }
      : null,
    profile.following || profile.following === 0
      ? { label: 'Following', value: formatNumber(profile.following) }
      : null,
    profile.location ? { label: '所在地', value: profile.location } : null,
    profile.company ? { label: '公司/组织', value: profile.company } : null,
    profile.createdAt ? { label: '加入 GitHub', value: formatDate(profile.createdAt) } : null,
    blogUrl
      ? {
        href: blogUrl,
        label: '个人链接',
        value: profile.blog,
      }
      : null,
  ].filter(Boolean)
}

function LanguageTooltip({ active, payload }) {
  if (!active || !payload?.length) return null
  const item = payload[0].payload
  return (
    <div className="github-chart-tooltip">
      <strong>{item.language}</strong>
      <span>{item.count} 个仓库 · {item.percentage}%</span>
    </div>
  )
}

function GithubAccountReport({ result, refreshing }) {
  const profile = useMemo(() => result.profile || {}, [result.profile])
  const repos = useMemo(
    () => (Array.isArray(result.repos) ? result.repos : []),
    [result.repos],
  )
  const analysis = useMemo(
    () => result.analysis || {},
    [result.analysis],
  )
  const profileUrl = profile.htmlUrl
    || `https://github.com/${encodeURIComponent(result.githubUsername)}`
  const languageStats = useMemo(() => getLanguageStats(repos), [repos])
  const resumeRepos = useMemo(
    () => getResumeRepositories(analysis, repos),
    [analysis, repos],
  )
  const profileFacts = useMemo(
    () => getProfileFacts(profile, repos),
    [profile, repos],
  )
  const now = Date.now()
  const recentWindow = RECENT_ACTIVE_DAYS * 24 * 60 * 60 * 1000
  const totalStars = repos.reduce(
    (total, repo) => total + Number(repo.stars || 0),
    0,
  )
  const totalForks = repos.reduce(
    (total, repo) => total + Number(repo.forks || 0),
    0,
  )
  const totalIssues = repos.reduce(
    (total, repo) => total + Number(repo.openIssues || 0),
    0,
  )
  const originalRepos = repos.filter((repo) => !repo.fork).length
  const forkedRepos = repos.length - originalRepos
  const archivedRepos = repos.filter((repo) => repo.archived).length
  const recentlyActiveRepos = repos.filter((repo) => {
    const time = getTime(repo.pushedAt || repo.updatedAt)
    return time > 0 && now - time <= recentWindow
  }).length
  const latestRepoUpdate = [...repos]
    .map((repo) => repo.pushedAt || repo.updatedAt)
    .filter(Boolean)
    .sort((first, second) => getTime(second) - getTime(first))[0]
  const primaryLanguages = languageStats.slice(0, 5)
  const analysisTime = formatDateTime(result.analyzedAt)
  const latestUpdateLabel = formatDate(latestRepoUpdate)

  return (
    <article
      className={`github-analysis-report github-account-report ${refreshing ? 'is-refreshing' : ''}`}
      aria-busy={refreshing}
    >
      <div className="github-report-notice">
        <Lightbulb size={18} />
        <p>{UPDATE_NOTICE}</p>
      </div>

      {refreshing && (
        <p className="github-refresh-progress" role="status">
          <LoaderCircle className="spin" size={15} />
          正在重新拉取 GitHub 数据并生成分析，完成后会自动更新本页。
        </p>
      )}

      <ReportSection
        description="来自已保存的 GitHub 公开资料，不会在进入页面时自动重新抓取。"
        icon={<UserRound size={20} />}
        title="GitHub 基础信息"
      >
        <div className="github-profile-card">
          <div className="github-profile-identity">
            {profile.avatarUrl ? (
              <img src={profile.avatarUrl} alt={`${result.githubUsername} GitHub 头像`} />
            ) : (
              <span className="github-report-avatar-placeholder">
                <UserRound size={28} />
              </span>
            )}
            <div>
              <h2>{profile.name || result.githubUsername}</h2>
              <a href={profileUrl} target="_blank" rel="noreferrer">
                @{result.githubUsername}
                <ExternalLink size={14} />
              </a>
              {profile.bio && <p>{profile.bio}</p>}
              {primaryLanguages.length > 0 && (
                <div className="github-profile-language-strip">
                  {primaryLanguages.map((item) => (
                    <Badge key={item.language} variant="secondary">
                      {item.language}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>

          {profileFacts.length > 0 && (
            <dl className="github-profile-facts">
              {profileFacts.map((item) => (
                <div key={item.label}>
                  <dt>{item.label}</dt>
                  <dd>
                    {item.href ? (
                      <a href={item.href} target="_blank" rel="noreferrer">
                        {item.value}
                        <ExternalLink size={12} />
                      </a>
                    ) : item.value}
                  </dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      </ReportSection>

      <section className="github-report-stats-grid" aria-label="核心指标">
        {repos.length > 0 && (
          <StatsCard
            icon={<FolderGit2 size={18} />}
            label="仓库数量"
            value={formatNumber(repos.length)}
            hint={`${formatNumber(originalRepos)} 原创 / ${formatNumber(forkedRepos)} Fork`}
          />
        )}
        {languageStats.length > 0 && (
          <StatsCard
            icon={<Layers3 size={18} />}
            label="主要语言数量"
            tone="success"
            value={formatNumber(languageStats.length)}
            hint={primaryLanguages.map((item) => item.language).join('、')}
          />
        )}
        {(totalStars > 0 || repos.length > 0) && (
          <StatsCard
            icon={<Star size={18} />}
            label="总 Star"
            value={formatNumber(totalStars)}
            hint={`${formatNumber(totalForks)} Fork`}
          />
        )}
        {repos.length > 0 && (
          <StatsCard
            icon={<Activity size={18} />}
            label="活跃项目数"
            tone="success"
            value={formatNumber(recentlyActiveRepos)}
            hint={`近 ${RECENT_ACTIVE_DAYS} 天有更新`}
          />
        )}
        {latestUpdateLabel && (
          <StatsCard
            icon={<CalendarDays size={18} />}
            label="最近项目更新"
            value={latestUpdateLabel}
            hint={analysisTime ? `报告生成：${analysisTime}` : ''}
          />
        )}
      </section>

      <div className="github-report-main-grid">
        <div className="github-report-main-column">
          <ReportSection
            className="github-tech-report-section"
            description="按公开仓库主语言统计，帮助判断账号的技术重心。"
            icon={<Code2 size={20} />}
            title="技术栈分析"
          >
            <div className="github-stack-layout">
              {languageStats.length ? (
                <div className="github-language-chart-card">
                  <ResponsiveContainer height={260} width="100%">
                    <PieChart>
                      <Pie
                        cx="50%"
                        cy="50%"
                        data={languageStats}
                        dataKey="count"
                        innerRadius={62}
                        nameKey="language"
                        outerRadius={94}
                        paddingAngle={2}
                      >
                        {languageStats.map((item, index) => (
                          <Cell
                            fill={CHART_COLORS[index % CHART_COLORS.length]}
                            key={item.language}
                          />
                        ))}
                      </Pie>
                      <Tooltip content={<LanguageTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <p className="github-report-empty-text">公开仓库暂未标注语言。</p>
              )}

              <div className="github-language-stats">
                {languageStats.length ? languageStats.map((item, index) => (
                  <div key={item.language}>
                    <div>
                      <span
                        className="github-language-dot"
                        style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}
                      />
                      <strong>{item.language}</strong>
                      <span>{item.count} 个仓库 · {item.percentage}%</span>
                    </div>
                    <i><span style={{ width: `${item.percentage}%` }} /></i>
                  </div>
                )) : null}
              </div>
            </div>

            <div className="github-subpanel">
              <h3><PieChartIcon size={16} /> 技术方向观察</h3>
              <SimpleList
                items={analysis.languageInsights}
                emptyText="本次没有生成语言方向观察。"
              />
            </div>
          </ReportSection>

          <ReportSection
            description="从活跃度、原创比例、Star、Issue 和归档状态做结构化展示。"
            icon={<CheckCircle2 size={20} />}
            title="项目质量分析"
          >
            <MarkdownBlock
              className="github-analysis-lead"
              content={analysis.overview || analysis.summary || result.summary}
            />

            <div className="github-quality-metrics">
              <article>
                <FolderGit2 size={17} />
                <strong>{formatNumber(originalRepos)}</strong>
                <span>原创项目</span>
              </article>
              <article>
                <Activity size={17} />
                <strong>{formatNumber(recentlyActiveRepos)}</strong>
                <span>近半年活跃</span>
              </article>
              <article>
                <Archive size={17} />
                <strong>{formatNumber(archivedRepos)}</strong>
                <span>归档仓库</span>
              </article>
              <article>
                <GitBranch size={17} />
                <strong>{formatNumber(totalIssues)}</strong>
                <span>公开 Issue</span>
              </article>
            </div>

            <div className="github-analysis-two-column">
              <div className="github-subpanel strength-section">
                <h3><TrendingUp size={16} /> 可以展示的优势</h3>
                <SimpleList
                  items={analysis.strengths}
                  emptyText="本次没有生成明确的优势。"
                />
              </div>
              <div className="github-subpanel weakness-section">
                <h3><Target size={16} /> 需要补强的地方</h3>
                <SimpleList
                  items={analysis.improvementSuggestions}
                  emptyText="本次没有生成改进建议。"
                />
              </div>
            </div>
          </ReportSection>

          <ReportSection
            description="优先展示 AI 分析结果中明确推荐的仓库。"
            icon={<Rocket size={20} />}
            title="适合写进简历的项目"
          >
            {resumeRepos.length ? (
              <div className="github-resume-project-grid">
                {resumeRepos.map((repo) => (
                  <article key={repo.githubId || repo.fullName || repo.name}>
                    <header>
                      <div>
                        <h3>{repo.name || repoLabel(repo)}</h3>
                        <small>{repo.fullName || repo.name}</small>
                      </div>
                      {repo.htmlUrl && (
                        <a
                          href={repo.htmlUrl}
                          target="_blank"
                          rel="noreferrer"
                          aria-label={`打开 ${repoLabel(repo)}`}
                        >
                          <ExternalLink size={15} />
                        </a>
                      )}
                    </header>
                    {repo.description && <p>{repo.description}</p>}
                    {repo.resumeReason && (
                      <div className="github-resume-reason">
                        <span>推荐理由</span>
                        <p>{repo.resumeReason}</p>
                      </div>
                    )}
                    {repo.resumeAngle && (
                      <div className="github-resume-reason">
                        <span>简历包装角度</span>
                        <p>{repo.resumeAngle}</p>
                      </div>
                    )}
                    <footer>
                      {repo.language && <span><Code2 size={13} /> {repo.language}</span>}
                      <span><Star size={13} /> {formatNumber(repo.stars)}</span>
                      <span><GitFork size={13} /> {formatNumber(repo.forks)}</span>
                    </footer>
                  </article>
                ))}
              </div>
            ) : (
              <EmptyState
                icon={<Rocket size={28} />}
                message="当前保存的分析结果里没有明确推荐项目。刷新分析后，如果公开仓库数据足够，AI 会重新判断代表项目。"
                title="暂无可展示的推荐项目"
              />
            )}
          </ReportSection>

          <ReportSection
            className="github-job-section"
            description="将 AI 建议拆成求职时更容易执行的几个面向。"
            icon={<Briefcase size={20} />}
            title="求职建议"
          >
            <div className="github-job-advice">
              <div className="github-subpanel">
                <h3><Sparkles size={16} /> 当前优势</h3>
                <SimpleList
                  items={analysis.strengths}
                  emptyText="本次没有生成优势方向。"
                />
              </div>
              <div className="github-subpanel">
                <h3><Target size={16} /> 需要补强</h3>
                <SimpleList
                  items={analysis.improvementSuggestions}
                  emptyText="本次没有生成补强建议。"
                />
              </div>
              <div className="github-subpanel">
                <h3><Users size={16} /> 适合岗位</h3>
                <p className="github-report-empty-text">
                  当前分析结果没有单独给出岗位方向，暂不根据仓库数据臆测岗位。
                </p>
              </div>
              <div className="github-subpanel">
                <h3><ListChecks size={16} /> 30 天优化建议</h3>
                <SimpleList
                  items={analysis.nextSteps}
                  emptyText="本次没有生成下一步行动。"
                />
              </div>
            </div>
          </ReportSection>

          <ReportSection
            className="github-ai-summary-card"
            description="来自已保存 AI 分析结果，支持 Markdown 排版。"
            icon={<BrainCircuit size={20} />}
            title="AI 总结"
          >
            <MarkdownBlock
              className="github-ai-summary-body"
              content={result.summary || analysis.summary}
              fallback="本次分析没有生成摘要。"
            />
            {analysis.conclusion && (
              <MarkdownBlock
                className="github-ai-conclusion"
                content={analysis.conclusion}
              />
            )}
          </ReportSection>
        </div>

        <aside className="github-report-sidebar">
          <section className="github-sidebar-card">
            <h2>报告状态</h2>
            <dl>
              {analysisTime && (
                <div>
                  <dt>上次分析时间</dt>
                  <dd>{analysisTime}</dd>
                </div>
              )}
              {latestUpdateLabel && (
                <div>
                  <dt>最近项目更新</dt>
                  <dd>{latestUpdateLabel}</dd>
                </div>
              )}
              {repos.length > 0 && (
                <div>
                  <dt>已读取仓库</dt>
                  <dd>{formatNumber(repos.length)}</dd>
                </div>
              )}
            </dl>
          </section>

          <section className="github-sidebar-card">
            <h2>公开数据概览</h2>
            <div className="github-sidebar-metrics">
              <span><Star size={14} /> {formatNumber(totalStars)} Stars</span>
              <span><GitFork size={14} /> {formatNumber(totalForks)} Forks</span>
              <span><Globe2 size={14} /> {formatNumber(languageStats.length)} Languages</span>
              <span><Archive size={14} /> {formatNumber(archivedRepos)} Archived</span>
            </div>
          </section>

          {primaryLanguages.length > 0 && (
            <section className="github-sidebar-card">
              <h2>主要语言</h2>
              <div className="github-sidebar-badges">
                {primaryLanguages.map((item) => (
                  <Badge key={item.language} variant="secondary">
                    {item.language}
                    <span>{item.percentage}%</span>
                  </Badge>
                ))}
              </div>
            </section>
          )}
        </aside>
      </div>
    </article>
  )
}

function MyGithubPage() {
  const { user } = useAuth()
  const { setAssistantRepo } = useOutletContext() || {}
  const [githubUsername, setGithubUsername] = useState(
    user?.githubUsername || '',
  )
  const [result, setResult] = useState(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [refreshError, setRefreshError] = useState('')
  const [refreshing, setRefreshing] = useState(false)

  useEffect(() => setPageMetadata({
    title: '我的 GitHub 分析 - 青途 AI',
    description: '基于当前登录用户的 GitHub 公开资料生成个人分析。',
    robots: NOINDEX_ROBOTS,
  }), [])

  useEffect(() => {
    if (!setAssistantRepo || !result?.repos?.length) return undefined
    const standout = getResumeRepositories(result.analysis || {}, result.repos)[0]
    const context = userGithubRepositoryAssistantContext(standout || result.repos[0], {
      summary: result.summary || result.analysis?.summary || '',
    })
    setAssistantRepo(context)
    return () => {
      setAssistantRepo((current) => (
        current?.fullName === context?.fullName ? null : current
      ))
    }
  }, [result, setAssistantRepo])

  const loadSavedAnalysis = useCallback(async (signal) => {
    setLoading(true)
    setLoadError('')
    try {
      const data = await getGithubAnalysis(signal)
      if (data.hasAnalysis) {
        setResult(data)
        setGithubUsername(data.githubUsername)
      } else {
        setResult(null)
        setGithubUsername((current) => current || user?.githubUsername || '')
      }
    } catch (error) {
      if (error.name !== 'AbortError') setLoadError(error.message)
    } finally {
      setLoading(false)
    }
  }, [user?.githubUsername])

  useEffect(() => {
    const controller = new AbortController()
    loadSavedAnalysis(controller.signal)
    return () => controller.abort()
  }, [loadSavedAnalysis])

  const runAnalysis = async (username) => {
    if (refreshing) return
    const normalizedUsername = String(username || '').trim()
    if (!normalizedUsername) {
      setRefreshError('请输入 GitHub 用户名。')
      return
    }

    setRefreshing(true)
    setRefreshError('')
    try {
      const data = await refreshGithubAnalysis(normalizedUsername)
      setResult(data)
      setGithubUsername(data.githubUsername)
      window.requestAnimationFrame(() => {
        document.querySelector('.github-account-report')
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    } catch (error) {
      setRefreshError(error.message || 'GitHub 分析暂时失败，请稍后重试。')
    } finally {
      setRefreshing(false)
    }
  }

  const submitAnalysis = (event) => {
    event.preventDefault()
    runAnalysis(githubUsername)
  }

  const resultProfileUrl = result
    ? result.profile?.htmlUrl
      || `https://github.com/${encodeURIComponent(result.githubUsername)}`
    : ''

  return (
    <div className="page-container my-github-page">
      <section className="my-github-hero">
        <div className="my-github-hero-copy">
          <span className="eyebrow">YOUR GITHUB / AI REPORT</span>
          <h1>我的 GitHub 开发者分析</h1>
          <p>
            基于你的 GitHub 公开仓库，分析技术栈、项目质量、简历价值和求职方向。
          </p>
          {result?.analyzedAt && (
            <div className="my-github-last-time">
              <Clock3 size={16} />
              上次分析时间：{formatDateTime(result.analyzedAt)}
            </div>
          )}
        </div>

        {result && !loading && !loadError && (
          <div className="my-github-hero-actions">
            <Button
              aria-label="刷新分析按钮"
              disabled={refreshing}
              onClick={() => runAnalysis(result.githubUsername)}
            >
              {refreshing
                ? <LoaderCircle className="spin" size={17} />
                : <RefreshCw size={17} />}
              {refreshing ? '分析中...' : '刷新分析'}
            </Button>
            <a
              className="github-report-link-button"
              href={resultProfileUrl}
              rel="noreferrer"
              target="_blank"
            >
              <GitBranch size={17} />
              GitHub 主页
              <ExternalLink size={14} />
            </a>
          </div>
        )}

        {!result && !loading && !loadError && (
          <form className="my-github-start-form" onSubmit={submitAnalysis}>
            <label htmlFor="github-analysis-username">GitHub 用户名</label>
            <div>
              <input
                id="github-analysis-username"
                name="githubUsername"
                type="text"
                value={githubUsername}
                disabled={refreshing}
                autoComplete="off"
                maxLength={39}
                placeholder="例如：octocat"
                onChange={(event) => setGithubUsername(event.target.value)}
              />
              <Button type="submit" disabled={refreshing}>
                {refreshing
                  ? <LoaderCircle className="spin" size={17} />
                  : <BrainCircuit size={17} />}
                {refreshing ? '分析中...' : '开始分析'}
              </Button>
            </div>
            <p>只有点击按钮后才会读取 GitHub 数据并调用 AI。</p>
          </form>
        )}
      </section>

      {loading && <LoadingState label="正在读取上次 GitHub 分析结果" />}
      {!loading && loadError && (
        <ErrorState
          message={loadError}
          onRetry={() => loadSavedAnalysis()}
        />
      )}

      {!loading && !loadError && !result && (
        <div className="github-report-notice my-github-empty-notice">
          <Lightbulb size={18} />
          <p>{UPDATE_NOTICE}</p>
        </div>
      )}

      {refreshing && !result && (
        <p className="github-refresh-progress" role="status">
          <LoaderCircle className="spin" size={15} />
          正在拉取 GitHub 数据并生成分析。
        </p>
      )}

      {refreshError && (
        <p className="github-analysis-error" role="alert">{refreshError}</p>
      )}

      {!loading && !loadError && result && (
        <GithubAccountReport
          result={result}
          refreshing={refreshing}
        />
      )}
    </div>
  )
}

export default MyGithubPage
